import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import {LoadingButton} from "@mui/lab";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import {BaseModel} from "crqlar-react-common/dist/models/common/base.model";
import {UseEntityData} from "crqlar-react-common/dist/components/useEntity";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {useUnsavedChangesWarning} from "crqlar-react-common/dist/utils/useUnsavedChangesWarning";
import UnsavedChangesDialog from "crqlar-react-common/dist/components/UnsavedChangesDialog";
import {ReactNode, useEffect, useRef} from "react";
import {useTranslation} from "react-i18next";
import {closeSnackbar, enqueueSnackbar} from "notistack";
import SettingsPageHeader from "./SettingsPageHeader";
import {useSettingsHeaderContext} from "./SettingsHeaderContext";

type Props<T extends BaseModel> = {
    title: string | null | undefined
    back: () => void
    data: UseEntityData<T>
    helpUrl?: string
    restrictEdit?: boolean
    forceEnableSave?: boolean
    extraActions?: ReactNode
}

const SettingsEntityHeader = <T extends BaseModel>({
    title,
    back,
    data,
    helpUrl,
    restrictEdit,
    forceEnableSave,
    extraActions,
}: Props<T>) => {
    const {t} = useTranslation()
    const readOnly = useReadOnly()
    const {hostElement, isMobile} = useSettingsHeaderContext()
    const actionsRef = useRef<HTMLDivElement>(null)

    // warn before route changes / reload while the entity holds edits the user could still save
    const {showDialog, handleConfirmNavigation, handleCancelNavigation} = useUnsavedChangesWarning({
        hasUnsavedChanges: (data.isDirty || !!forceEnableSave) && !data.isMissing && !readOnly && !restrictEdit,
        isSaving: data.saving,
    })

    useEffect(() => {
        if (!readOnly) return
        const scope = actionsRef.current?.closest('[data-readonly-lock-scope]') as HTMLElement | null
        if (!scope) return
        scope.setAttribute('data-readonly-locked', 'true')
        return () => scope.removeAttribute('data-readonly-locked')
    }, [hostElement, readOnly])

    const anchorOrigin = {vertical: 'bottom', horizontal: 'center'} as const

    const handleSave = async () => {
        const previousItem = {...data.originalItem}
        const canUndo = !data.isNew
        const result = await data.save()

        if (!result) {
            enqueueSnackbar(t('Changes could not be saved. Check all fields!'), {variant: 'error', anchorOrigin})
            return
        }

        if (!canUndo) {
            enqueueSnackbar(t('Changes saved!'), {variant: 'success', anchorOrigin})
            return
        }

        enqueueSnackbar(t('Changes saved!'), {
            variant: 'success',
            anchorOrigin,
            action: snackbarId => (
                <Button
                    size="small"
                    sx={{color: 'inherit', fontWeight: 600}}
                    onClick={async () => {
                        closeSnackbar(snackbarId)
                        const reverted = await data.saveThenSet(previousItem)
                        enqueueSnackbar(
                            reverted ? t('Changes undone') : t('Changes could not be saved. Check all fields!'),
                            {variant: reverted ? 'info' : 'error', anchorOrigin}
                        )
                    }}
                >
                    {t('Undo')}
                </Button>
            ),
        })
    }

    const saveAction = (
        <LoadingButton
            id="btn-entity-save"
            size={isMobile ? 'small' : undefined}
            startIcon={isMobile ? undefined : data.isNew ? <AddIcon/> : <SaveIcon/>}
            disabled={data.loading || (!data.isDirty && !forceEnableSave) || !data.isValid || readOnly || restrictEdit}
            loading={data.saving}
            onClick={handleSave}
            variant="contained"
            color={data.isNew ? "primary" : "secondary"}
            sx={isMobile ? {minWidth: 0, px: 1.5} : undefined}
        >
            {t(data.isNew ? 'Create new' : 'Save')}
        </LoadingButton>
    )

    const actions = data.isMissing ? undefined : (
        <Stack ref={actionsRef} direction="row" alignItems="center" gap={isMobile ? 0.5 : 1}>
            {isMobile && saveAction}

            {extraActions}

            {helpUrl && (
                <Tooltip title={t('Help')}>
                    <IconButton
                        id="btn-entity-help"
                        size="small"
                        onClick={() => window.open(helpUrl, '_blank', 'noopener')}
                        sx={{
                            width: isMobile ? 36 : 36.5,
                            height: isMobile ? 36 : 36.5,
                            borderRadius: 1,
                        }}
                    >
                        <InfoOutlinedIcon fontSize="small"/>
                    </IconButton>
                </Tooltip>
            )}

            {!isMobile && saveAction}
        </Stack>
    )

    return (
        <>
            <SettingsPageHeader
                title={title}
                back={back}
                actions={actions}
                loading={data.loading || data.saving}
            />
            {data.renderSnackbars()}
            <UnsavedChangesDialog
                open={showDialog}
                onConfirm={handleConfirmNavigation}
                onCancel={handleCancelNavigation}
            />
        </>
    )
}

export default SettingsEntityHeader
